/**
 * Barra de Busca Avançada com Autocomplete
 */

import React, { useState, useRef, useEffect, useCallback } from 'react'
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Typography,
  Popper,
  ClickAwayListener,
  Fade,
  CircularProgress,
  Tooltip,
  Divider,
  alpha,
  useMediaQuery,
  useTheme
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import HistoryIcon from '@mui/icons-material/History'
import PersonIcon from '@mui/icons-material/Person'
import FolderIcon from '@mui/icons-material/Folder'
import DescriptionIcon from '@mui/icons-material/Description'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import BusinessIcon from '@mui/icons-material/Business'
import EventIcon from '@mui/icons-material/Event'
import TuneIcon from '@mui/icons-material/Tune'
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn'
import { SearchSuggestion, EntityType } from '../types/search.types'

interface SearchBarProps {
  /** Valor atual da busca */
  value: string
  /** Callback quando o valor muda */
  onChange: (value: string) => void
  /** Callback quando o usuário pressiona Enter ou clica em buscar */
  onSearch: () => void
  /** Sugestões de autocomplete */
  suggestions?: SearchSuggestion[]
  /** Estado de loading */
  isSearching?: boolean
  /** Placeholder */
  placeholder?: string
  /** Mostra botão de filtros avançados */
  showFiltersButton?: boolean
  /** Callback para abrir filtros */
  onOpenFilters?: () => void
  /** Tamanho */
  size?: 'small' | 'medium'
  /** Fullwidth */
  fullWidth?: boolean
  /** Autofocar */
  autoFocus?: boolean
}

const ENTITY_ICONS: Record<string, React.ReactNode> = {
  'PERSON': <PersonIcon fontSize="small" sx={{ color: '#1976d2' }} />,
  'ORGANIZATION': <BusinessIcon fontSize="small" sx={{ color: '#7b1fa2' }} />,
  'LOCATION': <LocationOnIcon fontSize="small" sx={{ color: '#d32f2f' }} />,
  'DATE': <EventIcon fontSize="small" sx={{ color: '#ed6c02' }} />,
  'history': <HistoryIcon fontSize="small" sx={{ color: '#757575' }} />,
  'file': <DescriptionIcon fontSize="small" sx={{ color: '#2e7d32' }} />,
  'speaker': <PersonIcon fontSize="small" sx={{ color: '#0288d1' }} />,
  'entity': <FolderIcon fontSize="small" sx={{ color: '#9c27b0' }} />,
  'term': <SearchIcon fontSize="small" sx={{ color: '#455a64' }} />
}

export default function SearchBar({
  value,
  onChange,
  onSearch,
  suggestions = [],
  isSearching = false,
  placeholder = 'Buscar em transcrições, entidades, análises...',
  showFiltersButton = true,
  onOpenFilters,
  size = 'medium',
  fullWidth = true,
  autoFocus = false
}: SearchBarProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)
  const anchorRef = useRef<HTMLDivElement>(null)

  // ═══════════════════════════════════════════════════════════════
  // HANDLERS
  // ═══════════════════════════════════════════════════════════════

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    onChange(newValue)
    setShowSuggestions(newValue.length > 0)
    setSelectedIndex(-1)
  }

  const handleClear = () => {
    onChange('')
    setShowSuggestions(false)
    setSelectedIndex(-1)
    inputRef.current?.focus()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (selectedIndex >= 0 && suggestions[selectedIndex]) {
        handleSelectSuggestion(suggestions[selectedIndex])
      } else {
        onSearch()
        setShowSuggestions(false)
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex(prev =>
        prev < suggestions.length - 1 ? prev + 1 : prev
      )
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex(prev => prev > 0 ? prev - 1 : -1)
    } else if (e.key === 'Escape') {
      setShowSuggestions(false)
      setSelectedIndex(-1)
    }
  }

  const handleSelectSuggestion = (suggestion: SearchSuggestion) => {
    onChange(suggestion.text)
    setShowSuggestions(false)
    setSelectedIndex(-1)

    // Executa busca após selecionar sugestão
    setTimeout(() => onSearch(), 100)
  }

  const handleFocus = () => {
    if (value.length > 0 || suggestions.length > 0) {
      setShowSuggestions(true)
    }
  }

  const handleClickAway = () => {
    setShowSuggestions(false)
    setSelectedIndex(-1)
  }

  // ═══════════════════════════════════════════════════════════════
  // KEYBOARD SHORTCUT (Ctrl+K)
  // ═══════════════════════════════════════════════════════════════

  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        inputRef.current?.focus()
      }
    }

    window.addEventListener('keydown', handleGlobalKeyDown)
    return () => window.removeEventListener('keydown', handleGlobalKeyDown)
  }, [])

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════

  const getSuggestionIcon = (suggestion: SearchSuggestion) => {
    if (suggestion.metadata?.entityType) {
      return ENTITY_ICONS[suggestion.metadata.entityType] || ENTITY_ICONS.entity
    }
    return ENTITY_ICONS[suggestion.type] || <SearchIcon fontSize="small" />
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box ref={anchorRef} sx={{ position: 'relative', width: fullWidth ? '100%' : 'auto' }}>
        <TextField
          inputRef={inputRef}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder}
          size={size}
          fullWidth={fullWidth}
          autoFocus={autoFocus}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'background.paper',
              borderRadius: 2,
              transition: 'all 0.2s ease',
              '&:hover': {
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              },
              '&.Mui-focused': {
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.15)'
              }
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {isSearching ? (
                  <CircularProgress size={20} />
                ) : (
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                )}
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {/* Atalho de teclado - escondido em mobile */}
                {!value && !isMobile && (
                  <Chip
                    label="Ctrl+K"
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.7rem',
                      bgcolor: alpha('#000', 0.06),
                      mr: 1
                    }}
                  />
                )}

                {/* Botão limpar */}
                {value && (
                  <IconButton size="small" onClick={handleClear}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}

                {/* Botão filtros */}
                {showFiltersButton && onOpenFilters && (
                  <Tooltip title="Filtros avançados">
                    <IconButton size="small" onClick={onOpenFilters}>
                      <TuneIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </InputAdornment>
            )
          }}
        />

        {/* Dropdown de sugestões */}
        <Popper
          open={showSuggestions && suggestions.length > 0}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          transition
          style={{ width: anchorRef.current?.offsetWidth, zIndex: 1500 }}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={200}>
              <Paper
                elevation={8}
                sx={{
                  mt: 0.5,
                  borderRadius: 2,
                  overflow: 'hidden',
                  maxHeight: 400,
                  overflowY: 'auto'
                }}
              >
                <List dense disablePadding>
                  {suggestions.map((suggestion, index) => (
                    <ListItem
                      key={`${suggestion.type}-${suggestion.text}-${index}`}
                      button
                      selected={index === selectedIndex}
                      onClick={() => handleSelectSuggestion(suggestion)}
                      sx={{
                        py: 1,
                        '&.Mui-selected': {
                          bgcolor: alpha('#1976d2', 0.08)
                        },
                        '&:hover': {
                          bgcolor: alpha('#000', 0.04)
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {getSuggestionIcon(suggestion)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" noWrap>
                            {suggestion.text}
                          </Typography>
                        }
                        secondary={
                          suggestion.metadata?.count && (
                            <Typography variant="caption" color="text.secondary">
                              {suggestion.metadata.count} ocorrências
                            </Typography>
                          )
                        }
                      />
                      {index === selectedIndex && (
                        <KeyboardReturnIcon
                          fontSize="small"
                          sx={{ color: 'text.disabled', ml: 1 }}
                        />
                      )}
                    </ListItem>
                  ))}
                </List>

                {/* Dica de sintaxe - escondido em mobile */}
                {!isMobile && (
                  <>
                    <Divider />
                    <Box sx={{ p: 1.5, bgcolor: alpha('#000', 0.02) }}>
                      <Typography variant="caption" color="text.secondary">
                        Dica: Use <code>falante:"Nome"</code> para filtrar por falante,
                        ou <code>"frase exata"</code> para busca exata
                      </Typography>
                    </Box>
                  </>
                )}
              </Paper>
            </Fade>
          )}
        </Popper>
      </Box>
    </ClickAwayListener>
  )
}
